import {
  U21Avatar,
  U21Chip,
  U21ChipProps,
  U21MenuLayout,
  U21MenuList,
  U21MenuListOptionProps,
} from 'app/shared/u21-ui/components';
import { useReassignCase } from 'app/modules/cases/queries/useReassignCase';
import { useGetCase } from 'app/modules/cases/queries/useGetCase';
import { useSelector } from 'react-redux';
import { selectHasReassignCasePermission } from 'app/modules/cases/selectors';
import { IconBriefcaseArrow } from 'app/shared/components/Icons/IconBriefcaseArrow';
import { useMemo, useState } from 'react';
import { getAgentDisplayName } from 'app/modules/agents/utils';
import { AgentStatus } from 'app/modules/agents/models';
import {
  DEFAULT_OFFSET,
  DEFAULT_PAGINATION_LIMIT,
} from 'app/shared/pagination/constants';
import { useAgentSearch } from 'app/modules/agents/queries/useAgentSearch';
import { AgentChip } from 'app/shared/components/AgentChip';

interface Props {
  id: number;
  onSuccess?: () => void;
}

export const Reassign = (props: Props) => {
  const { id, onSuccess } = props;

  const hasReassignCasePermission = useSelector(
    selectHasReassignCasePermission,
  );

  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const { case: c } = useGetCase(id);
  const {
    agents,
    query: { isLoading: agentsLoading },
  } = useAgentSearch({
    limit: DEFAULT_PAGINATION_LIMIT,
    offset: DEFAULT_OFFSET,
    query: [search],
    statuses: [AgentStatus.ACTIVE],
  });
  const { mutateAsync: reassignCase } = useReassignCase([id]);

  const options = useMemo<U21MenuListOptionProps[]>(() => {
    const onActionSuccess = async () => {
      setOpen(false);
      onSuccess?.();
    };

    return [
      {
        icon: <U21Avatar name="?" />,
        text: 'Unassigned',
        onClick: async (e) => {
          e.stopPropagation();
          try {
            await reassignCase(-1);
            await onActionSuccess();
          } catch {}
        },
      },
      ...agents.map((i) => {
        const name = getAgentDisplayName(i);
        return {
          text: name,
          onClick: async (e) => {
            e.stopPropagation();
            try {
              await reassignCase(i.id);
              await onActionSuccess();
            } catch {}
          },
          icon: <U21Avatar name={name} src={i.picture} />,
        };
      }),
    ];
  }, [agents, onSuccess, reassignCase]);

  if (!c) {
    return null;
  }

  const commonChipProps: Partial<Omit<U21ChipProps, 'id'>> = {
    color: 'primary',
    variant: 'ghost',
  };

  if (
    !hasReassignCasePermission ||
    c.status === 'CLOSED' ||
    c.status === 'IN_REVIEW'
  ) {
    if (c.assigned_to_id > 0) {
      return (
        <AgentChip
          id={c.assigned_to_id}
          onClick={undefined}
          to={undefined}
          {...commonChipProps}
        />
      );
    }
    return (
      <U21Chip icon={<IconBriefcaseArrow />} {...commonChipProps}>
        Unassigned
      </U21Chip>
    );
  }

  return (
    <U21MenuLayout
      onClose={(e) => {
        e.stopPropagation();
        setOpen(false);
      }}
      trigger={
        c.assigned_to_id > 0 ? (
          <AgentChip
            id={c.assigned_to_id}
            onClick={() => setOpen(true)}
            to={undefined}
            {...commonChipProps}
          />
        ) : (
          <U21Chip
            icon={<IconBriefcaseArrow />}
            onClick={() => setOpen(true)}
            {...commonChipProps}
          >
            Unassigned
          </U21Chip>
        )
      }
      open={open}
    >
      <U21MenuList
        loading={agentsLoading}
        options={options}
        placeholder="Search agents"
        onSearch={setSearch}
      />
    </U21MenuLayout>
  );
};
